/**
 * The create form's voice note. A draft has no task id, so nothing uploads
 * here: the stopped recording is held, and once Crea succeeds the notes
 * section sends it as the task's first note (DD-050, TD-089). The recorder
 * is the notes section's VoiceRecorder without its Send.
 */

import React, { useCallback, useState } from "react";
import { MicIcon } from "@/components/icons";
import { VoiceRecorder, type VoiceRecording } from "@/core/voice";

interface TaskCreateVoiceProps {
  /** The stopped recording, or null once deleted, discarded or still running. */
  onRecordingChange: (recording: VoiceRecording | null) => void;
  /** True while a recording runs or is held. The drawer asks before closing on it. */
  onUnsentChange: (hasUnsent: boolean) => void;
}

const SECTION_LABEL = "text-[10px] font-semibold text-gray-500 uppercase tracking-[0.1em]";

export function TaskCreateVoice({ onRecordingChange, onUnsentChange }: TaskCreateVoiceProps) {
  const [open, setOpen] = useState(false);
  const [unsent, setUnsent] = useState(false);
  const [held, setHeld] = useState(false);
  const [confirmingClose, setConfirmingClose] = useState(false);

  const handleUnsentChange = useCallback(
    (hasUnsent: boolean) => {
      setUnsent(hasUnsent);
      onUnsentChange(hasUnsent);
    },
    [onUnsentChange]
  );
  const handleRecordedChange = useCallback(
    (recording: VoiceRecording | null) => {
      setHeld(recording !== null);
      onRecordingChange(recording);
    },
    [onRecordingChange]
  );

  // Unmounting the recorder reports the recording gone.
  const closeRecorder = () => {
    setConfirmingClose(false);
    setOpen(false);
  };

  return (
    <section>
      <h3 className={`${SECTION_LABEL} mb-2`}>Nota vocale</h3>
      {open ? (
        <div className="space-y-3">
          <VoiceRecorder onUnsentChange={handleUnsentChange} onRecordedChange={handleRecordedChange} />
          {held && <p className="text-xs text-gray-500">Diventa la prima nota del task.</p>}
          {confirmingClose ? (
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-700">Scartare la registrazione non inviata?</span>
              <button
                type="button"
                onClick={() => setConfirmingClose(false)}
                className="text-sm font-medium text-gray-500 hover:text-gray-700"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={closeRecorder}
                className="text-sm font-medium text-red-600 hover:text-red-700"
              >
                Scarta
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => (unsent ? setConfirmingClose(true) : closeRecorder())}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Senza nota vocale
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          title="Registra una nota vocale"
          className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700"
        >
          <MicIcon className="w-4 h-4" />
          Registra
        </button>
      )}
    </section>
  );
}
